import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { CircularProgress, colorSystem, Flexbox, TertiaryIconButton, Text } from '@luminovo/design-system';
import { Box, LinearProgress, Slide } from '@mui/material';
import * as Dialog from '@radix-ui/react-dialog';
import { Command } from 'cmdk';
import { ChevronRightIcon, InboxIcon, XIcon } from 'lucide-react';
import React, { type JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { isAccessoryCount, isIconConfig } from '../TanStackTable/CommandPalette/types';
import {
    formatAccessoryCount,
    HintButton,
    injectCmdkStyles,
    KeyboardBadge,
    matchShortcut,
    useIsMac,
    useKeyboardNavigation,
    usePaletteAnalytics,
    usePaletteOpenEffect,
    VisuallyHidden,
} from './shared';
import { GlobalCommandContext, GlobalCommandDefinition, GlobalCommandPaletteProps, SearchableResource } from './types';

injectCmdkStyles();

/** App-wide navigation and entity search. */
export function GlobalCommandPalette({
    open,
    onOpenChange,
    commands,
    searchableResources,
    placeholder,
    telemetry,
}: GlobalCommandPaletteProps): JSX.Element | null {
    const [search, setSearch] = useState('');
    const [selectedValue, setSelectedValue] = useState<string>('');
    const inputRef = useRef<HTMLInputElement>(null);
    const listContainerRef = useRef<HTMLDivElement>(null);

    const [pages, setPages] = useState<string[]>([]);
    const page = pages[pages.length - 1];

    const [loading, setLoading] = useState<string | null>(null);

    const [quickLookCommandId, setQuickLookCommandId] = useState<string | null>(null);
    const [selectedCommandId, setSelectedCommandId] = useState<string | null>(null);

    const [confirmingCommand, setConfirmingCommand] = useState<GlobalCommandDefinition | null>(null);

    // Whether Enter was pressed with Cmd/Ctrl, to open in a new tab.
    const modifierKeyRef = useRef(false);

    const searchTrackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const isMac = useIsMac();
    const { isKeyboardNavigating, setIsKeyboardNavigating, handleMouseMove } = useKeyboardNavigation();
    const { trackEvent } = usePaletteAnalytics({ telemetry, paletteType: 'global' });

    useEffect(() => {
        return () => {
            if (searchTrackTimeoutRef.current) {
                clearTimeout(searchTrackTimeoutRef.current);
            }
        };
    }, []);

    usePaletteOpenEffect(
        open,
        () => trackEvent('opened', { trigger: 'button' }),
        useCallback(() => {
            setSearch('');
            setSelectedValue('');
            setPages([]);
            setLoading(null);
            setQuickLookCommandId(null);
            setSelectedCommandId(null);
            setConfirmingCommand(null);
            setIsKeyboardNavigating(false);
            modifierKeyRef.current = false;
            if (searchTrackTimeoutRef.current) {
                clearTimeout(searchTrackTimeoutRef.current);
            }
        }, [setIsKeyboardNavigating]),
    );

    const handleClose = useCallback(() => {
        trackEvent('closed', { method: 'button' });
        onOpenChange(false);
    }, [onOpenChange, trackEvent]);

    const isOtherDialogOpen = useCallback(() => {
        return (
            document.querySelector('[cmdk-dialog]:not([data-global-palette])') !== null ||
            document.querySelector('[role="dialog"]:not([cmdk-dialog])') !== null
        );
    }, []);

    const isTableCommandPaletteActive = useCallback(() => {
        return document.querySelector('[data-table-command-palette-active]') !== null;
    }, []);

    useHotkeys(
        'mod+k',
        (e) => {
            if (isOtherDialogOpen()) return;
            if (isTableCommandPaletteActive()) return;
            e.preventDefault();
            if (open) {
                handleClose();
            } else {
                onOpenChange(true);
            }
        },
        {
            enableOnFormTags: true,
            enableOnContentEditable: false,
        },
        [open, onOpenChange, handleClose, isOtherDialogOpen, isTableCommandPaletteActive],
    );

    // Unlike Cmd+K, Cmd+P doesn't yield to a table whose selection bar claims Cmd+K.
    useHotkeys(
        'mod+p',
        (e) => {
            if (isOtherDialogOpen()) return;
            e.preventDefault();
            if (open) {
                handleClose();
            } else {
                onOpenChange(true);
            }
        },
        {
            enableOnFormTags: true,
            enableOnContentEditable: false,
        },
        [open, onOpenChange, handleClose, isOtherDialogOpen],
    );

    const createContext = useCallback(
        (): GlobalCommandContext => ({
            close: () => {
                trackEvent('closed', { method: 'programmatic' });
                onOpenChange(false);
            },
            openInNewTab: modifierKeyRef.current,
            goBack: () => {
                if (pages.length > 0) {
                    trackEvent('navigated_back', { method: 'programmatic' });
                    setPages((p) => p.slice(0, -1));
                }
            },
        }),
        [onOpenChange, trackEvent, pages.length],
    );

    const resolveChildren = useCallback(
        (cmd: GlobalCommandDefinition): GlobalCommandDefinition[] | null => {
            if (!cmd.children) return null;
            if (typeof cmd.children === 'function') {
                return cmd.children(createContext());
            }
            return cmd.children;
        },
        [createContext],
    );

    const hasChildren = useCallback((cmd: GlobalCommandDefinition): boolean => {
        if (!cmd.children) return false;
        if (typeof cmd.children === 'function') return true;
        return cmd.children.length > 0;
    }, []);

    const { currentCommands, parentCommand } = useMemo(() => {
        if (!page) return { currentCommands: commands, parentCommand: null };

        const findChildrenAndParent = (
            cmds: GlobalCommandDefinition[],
        ): { children: GlobalCommandDefinition[]; parent: GlobalCommandDefinition } | null => {
            for (const cmd of cmds) {
                if (cmd.id === page) {
                    return { children: resolveChildren(cmd) ?? [], parent: cmd };
                }
                const resolvedChildren = resolveChildren(cmd);
                if (resolvedChildren) {
                    const found = findChildrenAndParent(resolvedChildren);
                    if (found) return found;
                }
            }
            return null;
        };

        const result = findChildrenAndParent(commands);
        return {
            currentCommands: result?.children ?? [],
            parentCommand: result?.parent ?? null,
        };
    }, [commands, page, resolveChildren]);

    const emptyPlaceholder = useMemo(() => {
        if (!parentCommand?.emptyPlaceholder || currentCommands.length > 0) return null;
        return parentCommand.emptyPlaceholder;
    }, [parentCommand, currentCommands.length]);

    const groupedCommands = useMemo(() => {
        const ctx = createContext();
        const groups = new Map<string | null, GlobalCommandDefinition[]>();

        for (const cmd of currentCommands) {
            const isVisible = typeof cmd.visible === 'function' ? cmd.visible(ctx) : (cmd.visible ?? true);
            if (!isVisible) continue;

            if (!page && search) {
                const searchLower = search.toLowerCase();
                const label = cmd.label().toLowerCase();
                const description = cmd.description?.().toLowerCase() ?? '';
                const keywords = (cmd.keywords ?? []).join(' ').toLowerCase();
                const searchText = `${label} ${description} ${keywords}`;
                if (!searchText.includes(searchLower)) continue;
            }

            const groupName = cmd.group?.() ?? null;
            if (!groups.has(groupName)) {
                groups.set(groupName, []);
            }
            groups.get(groupName)!.push(cmd);
        }

        return Array.from(groups.entries());
    }, [currentCommands, search, page, createContext]);

    const visibleCommands = useMemo(() => {
        return groupedCommands.flatMap(([, cmds]) => cmds);
    }, [groupedCommands]);

    const findCommandById = useCallback(
        (id: string): GlobalCommandDefinition | null => {
            return visibleCommands.find((cmd) => cmd.id === id) ?? null;
        },
        [visibleCommands],
    );

    const selectedCommand = useMemo(() => {
        if (!selectedCommandId) return null;
        return findCommandById(selectedCommandId);
    }, [selectedCommandId, findCommandById]);

    const quickLookCommand = useMemo(() => {
        if (!quickLookCommandId) return null;
        return findCommandById(quickLookCommandId);
    }, [quickLookCommandId, findCommandById]);

    const selectedCommandHasQuickLook = useMemo(() => {
        return selectedCommand?.quickLook !== undefined;
    }, [selectedCommand]);

    // Quick Look follows the selection.
    useEffect(() => {
        if (!quickLookCommandId || !selectedCommandId) return;
        if (quickLookCommandId === selectedCommandId) return;

        const newSelectedCommand = findCommandById(selectedCommandId);
        if (newSelectedCommand?.quickLook) {
            // oxlint-disable-next-line react/set-state-in-effect -- SOU-4697: the extra render pass is kept deliberately; deriving this during render or resetting via `key` would change what the user sees
            setQuickLookCommandId(selectedCommandId);
        } else {
            setQuickLookCommandId(null);
        }
    }, [selectedCommandId, quickLookCommandId, findCommandById]);

    const openQuickLook = useCallback(
        (commandId: string) => {
            const cmd = findCommandById(commandId);
            if (!cmd?.quickLook) return;
            trackEvent('quick_look_opened', { commandId });
            setQuickLookCommandId(commandId);
        },
        [findCommandById, trackEvent],
    );

    const closeQuickLook = useCallback(
        (method: 'arrow_left' | 'escape' | 'command_change' | 'programmatic') => {
            if (!quickLookCommandId) return;
            trackEvent('quick_look_closed', { commandId: quickLookCommandId, method });
            setQuickLookCommandId(null);
            setTimeout(() => inputRef.current?.focus(), 0);
        },
        [quickLookCommandId, trackEvent],
    );

    const entityResults = useMemo(() => {
        if (!search || page) return [];

        const results: Array<{
            source: SearchableResource<unknown>;
            commands: GlobalCommandDefinition[];
        }> = [];

        for (const source of searchableResources) {
            const minQueryLength = source.minQueryLength ?? 3;
            if (search.length < minQueryLength) continue;

            const searchLower = search.toLowerCase();
            const maxResults = source.maxResults ?? 5;

            const matchingCommands: GlobalCommandDefinition[] = [];
            for (const entity of source.entities) {
                if (matchingCommands.length >= maxResults) break;

                const searchableText = source.getSearchableText(entity).toLowerCase();
                if (searchableText.includes(searchLower)) {
                    matchingCommands.push(source.toCommand(entity));
                }
            }

            if (matchingCommands.length > 0) {
                results.push({ source, commands: matchingCommands });
            }
        }

        return results;
    }, [searchableResources, search, page]);

    const handleSelect = useCallback(
        async (cmd: GlobalCommandDefinition) => {
            const ctx = createContext();
            const isDisabled = typeof cmd.disabled === 'function' ? cmd.disabled(ctx) : (cmd.disabled ?? false);
            const commandGroup = cmd.group?.() ?? null;

            if (isDisabled || loading) return;

            if (hasChildren(cmd)) {
                trackEvent('navigated_to_subcommands', { commandId: cmd.id });
                setPages((p) => [...p, cmd.id]);
                setSearch('');
                setQuickLookCommandId(null);
                return;
            }

            if (cmd.confirmation) {
                setConfirmingCommand(cmd);
                setQuickLookCommandId(null);
                return;
            }

            if (cmd.onSelect) {
                trackEvent('command_executed', { commandId: cmd.id, commandGroup, openInNewTab: ctx.openInNewTab });
                setLoading(cmd.id);

                try {
                    await cmd.onSelect(ctx);
                    trackEvent('command_succeeded', { commandId: cmd.id, commandGroup });
                    if (cmd.closeOnComplete === true) {
                        onOpenChange(false);
                    }
                } catch (error) {
                    trackEvent('command_failed', { commandId: cmd.id, commandGroup });
                    console.error('Command execution failed:', error);
                } finally {
                    setLoading(null);
                }
            }
        },
        [createContext, loading, hasChildren, trackEvent, onOpenChange],
    );

    const handleConfirm = useCallback(async () => {
        if (!confirmingCommand) return;
        const onSelect = confirmingCommand.onSelect;
        if (!onSelect) return;

        const ctx = createContext();
        const cmd = confirmingCommand;
        const commandGroup = cmd.group?.() ?? null;

        trackEvent('command_executed', { commandId: cmd.id, commandGroup, openInNewTab: ctx.openInNewTab });
        setLoading(cmd.id);

        try {
            await onSelect(ctx);
            trackEvent('command_succeeded', { commandId: cmd.id, commandGroup });
            if (cmd.closeOnComplete === true) {
                onOpenChange(false);
            } else {
                setConfirmingCommand(null);
            }
        } catch (error) {
            trackEvent('command_failed', { commandId: cmd.id, commandGroup });
            console.error('Command execution failed:', error);
        } finally {
            setLoading(null);
        }
    }, [confirmingCommand, createContext, onOpenChange, trackEvent]);

    const handleCancelConfirmation = useCallback(() => {
        setConfirmingCommand(null);
    }, []);

    const handleGoBack = useCallback(() => {
        if (pages.length > 0) {
            trackEvent('navigated_back', { method: 'button' });
            setPages((p) => p.slice(0, -1));
        }
    }, [pages.length, trackEvent]);

    const findCommandByShortcut = useCallback(
        (e: React.KeyboardEvent): GlobalCommandDefinition | null => {
            const ctx = createContext();

            for (const cmd of currentCommands) {
                if (!cmd.shortcut || cmd.shortcut.length === 0) continue;

                const isVisible = typeof cmd.visible === 'function' ? cmd.visible(ctx) : (cmd.visible ?? true);
                if (!isVisible) continue;

                const isDisabled = typeof cmd.disabled === 'function' ? cmd.disabled(ctx) : (cmd.disabled ?? false);
                if (isDisabled) continue;

                if (matchShortcut(cmd.shortcut, e, isMac)) return cmd;
            }
            return null;
        },
        [currentCommands, createContext, isMac],
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (['ArrowDown', 'ArrowUp', 'Tab'].includes(e.key)) {
                setIsKeyboardNavigating(true);
            }

            if (e.key === 'Enter') {
                modifierKeyRef.current = e.metaKey || e.ctrlKey;
            }

            if (e.key === 'Tab') {
                e.preventDefault();
                e.stopPropagation();

                const items = listContainerRef.current?.querySelectorAll('[cmdk-item]:not([data-disabled="true"])');
                if (!items || items.length === 0) return;

                const currentIndex = Array.from(items).findIndex(
                    (item) => item.getAttribute('data-selected') === 'true',
                );

                let nextIndex: number;
                if (e.shiftKey) {
                    nextIndex = currentIndex <= 0 ? items.length - 1 : currentIndex - 1;
                } else {
                    nextIndex = currentIndex >= items.length - 1 ? 0 : currentIndex + 1;
                }

                const nextItem = items[nextIndex];
                const nextValue = nextItem?.getAttribute('data-value');
                if (nextValue) {
                    setSelectedValue(nextValue);
                }
                return;
            }

            if (e.key === 'ArrowRight' && selectedCommandId && selectedCommandHasQuickLook && !quickLookCommandId) {
                e.preventDefault();
                e.stopPropagation();
                openQuickLook(selectedCommandId);
                return;
            }

            if (e.key === 'ArrowLeft' && quickLookCommandId) {
                e.preventDefault();
                e.stopPropagation();
                closeQuickLook('arrow_left');
                return;
            }

            if (e.key === 'Backspace' && !search && pages.length > 0) {
                e.preventDefault();
                trackEvent('navigated_back', { method: 'backspace' });
                setPages((p) => p.slice(0, -1));
                return;
            }

            const matchedCommand = findCommandByShortcut(e);
            if (matchedCommand) {
                e.preventDefault();
                e.stopPropagation();
                handleSelect(matchedCommand);
            }
        },
        [
            setIsKeyboardNavigating,
            search,
            pages.length,
            selectedCommandId,
            selectedCommandHasQuickLook,
            quickLookCommandId,
            openQuickLook,
            closeQuickLook,
            trackEvent,
            findCommandByShortcut,
            handleSelect,
        ],
    );

    // Refs, so handleOpenChange reads the current state synchronously.
    const pagesRef = useRef(pages);
    pagesRef.current = pages;
    const quickLookCommandIdRef = useRef(quickLookCommandId);
    quickLookCommandIdRef.current = quickLookCommandId;
    const confirmingCommandRef = useRef(confirmingCommand);
    confirmingCommandRef.current = confirmingCommand;

    // Escape first closes Quick Look, the confirmation or a subpage, and only then the palette.
    const handleOpenChange = useCallback(
        (newOpen: boolean) => {
            if (!newOpen && quickLookCommandIdRef.current) {
                trackEvent('quick_look_closed', { commandId: quickLookCommandIdRef.current, method: 'escape' });
                setQuickLookCommandId(null);
                setTimeout(() => inputRef.current?.focus(), 0);
                return;
            }
            if (!newOpen && confirmingCommandRef.current) {
                setConfirmingCommand(null);
                return;
            }
            if (!newOpen && pagesRef.current.length > 0) {
                trackEvent('navigated_back', { method: 'escape' });
                setPages((p) => p.slice(0, -1));
                setTimeout(() => inputRef.current?.focus(), 0);
                return;
            }
            if (!newOpen) {
                trackEvent('closed', { method: 'escape' });
            }
            onOpenChange(newOpen);
        },
        [onOpenChange, telemetry, trackEvent],
    );

    // We filter ourselves, so cmdk keeps every item.
    const customFilter = useCallback(() => 1, []);

    if (!open) return null;

    const ctx = createContext();

    return (
        <Command.Dialog
            open={open}
            onOpenChange={handleOpenChange}
            label={t`Command palette`}
            onKeyDown={handleKeyDown}
            onMouseMove={handleMouseMove}
            filter={customFilter}
            loop
            value={selectedValue}
            onValueChange={setSelectedValue}
            className={isKeyboardNavigating ? 'keyboard-navigating' : undefined}
            data-global-palette="true"
        >
            <VisuallyHidden>
                <Dialog.Title>{t`Command palette`}</Dialog.Title>
                <Dialog.Description>{t`Search for pages, projects, customers, and more`}</Dialog.Description>
            </VisuallyHidden>
            <Box
                sx={{
                    padding: '10px 20px 0 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    minHeight: 32,
                }}
            >
                <Flexbox
                    gap={4}
                    sx={{
                        alignItems: 'center',
                    }}
                >
                    {pages.length === 0 ? (
                        <Text variant="body-small" color={colorSystem.neutral[6]}>
                            <Trans>Search</Trans>
                        </Text>
                    ) : (
                        <>
                            <TertiaryIconButton onClick={handleGoBack} size="small" aria-label={t`Go back`}>
                                <ChevronRightIcon style={{ transform: 'rotate(180deg)' }} />
                            </TertiaryIconButton>
                            <Text variant="body-small" color={colorSystem.neutral[6]}>
                                {parentCommand?.label() ?? page}
                            </Text>
                        </>
                    )}
                </Flexbox>
                <TertiaryIconButton onClick={handleClose} size="small" aria-label={t`Close`}>
                    <XIcon />
                </TertiaryIconButton>
            </Box>
            <Box sx={{ position: 'relative' }}>
                <Command.Input
                    ref={inputRef}
                    value={search}
                    onValueChange={(value) => {
                        setSearch(value);
                        if (value) {
                            if (searchTrackTimeoutRef.current) {
                                clearTimeout(searchTrackTimeoutRef.current);
                            }
                            searchTrackTimeoutRef.current = setTimeout(() => {
                                trackEvent('searched', { query: value });
                            }, 300);
                        }
                    }}
                    placeholder={placeholder ?? t`Search...`}
                    autoFocus
                />
                {loading && (
                    <LinearProgress
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '2px',
                        }}
                    />
                )}
            </Box>
            <Box className="cmdk-quick-look-container">
                {confirmingCommand && (
                    <Box className="cmdk-quick-look-commands" ref={listContainerRef}>
                        <Box sx={{ padding: '16px 20px 8px 20px' }}>
                            <Text variant="body-semibold" color={colorSystem.neutral[9]}>
                                {confirmingCommand.confirmation?.title()}
                            </Text>
                            {confirmingCommand.confirmation?.description && (
                                <Text
                                    variant="body-small"
                                    color={colorSystem.neutral[6]}
                                    style={{ marginTop: 4, display: 'block' }}
                                >
                                    {confirmingCommand.confirmation.description()}
                                </Text>
                            )}
                        </Box>

                        <Command.List>
                            <Command.Empty>
                                <Trans>No actions found</Trans>
                            </Command.Empty>

                            <Command.Item
                                value="__confirmation_confirm__"
                                onSelect={handleConfirm}
                                disabled={loading !== null}
                            >
                                {loading === confirmingCommand.id && (
                                    <CircularProgress size="small" sx={{ flexShrink: 0 }} />
                                )}
                                <Box sx={{ flex: 1 }}>
                                    <Text
                                        variant="body"
                                        color={
                                            confirmingCommand.confirmation?.variant === 'destructive'
                                                ? colorSystem.red[7]
                                                : colorSystem.primary[7]
                                        }
                                    >
                                        {confirmingCommand.confirmation?.confirmLabel?.() ?? t`Confirm`}
                                    </Text>
                                </Box>
                            </Command.Item>

                            <Command.Item value="__confirmation_cancel__" onSelect={handleCancelConfirmation}>
                                <Text variant="body" color={colorSystem.neutral[6]}>
                                    ← {confirmingCommand.confirmation?.cancelLabel?.() ?? t`Cancel`}
                                </Text>
                            </Command.Item>
                        </Command.List>
                    </Box>
                )}

                {!confirmingCommand && (
                    <Box className="cmdk-quick-look-commands" ref={listContainerRef}>
                        <Command.List>
                            <Command.Empty>
                                <Trans>No results found</Trans>
                            </Command.Empty>

                            {pages.length > 0 && (
                                <Command.Item value="__back__" onSelect={handleGoBack}>
                                    <Text variant="body" color={colorSystem.neutral[6]}>
                                        ← <Trans>Back</Trans>
                                    </Text>
                                </Command.Item>
                            )}

                            {emptyPlaceholder && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '32px 16px',
                                        minHeight: '200px',
                                        textAlign: 'center',
                                    }}
                                >
                                    <InboxIcon size={48} color={colorSystem.neutral[4]} />
                                    <Flexbox
                                        gap="4px"
                                        sx={{
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Text variant="body-semibold" color={colorSystem.neutral[7]}>
                                            {emptyPlaceholder.title()}
                                        </Text>
                                        {emptyPlaceholder.description && (
                                            <Text variant="body-small" color={colorSystem.neutral[6]}>
                                                {emptyPlaceholder.description()}
                                            </Text>
                                        )}
                                    </Flexbox>
                                </Box>
                            )}

                            {groupedCommands.map(([groupName, cmds]) => {
                                const items = cmds.map((cmd) => (
                                    <GlobalCommandItem
                                        key={cmd.id}
                                        command={cmd}
                                        context={ctx}
                                        isMac={isMac}
                                        loading={loading === cmd.id}
                                        hasQuickLookOpen={quickLookCommandId === cmd.id}
                                        hasChildren={hasChildren(cmd)}
                                        onSelect={() => handleSelect(cmd)}
                                        onSelectedChange={(selected) => {
                                            if (selected) {
                                                setSelectedCommandId(cmd.id);
                                            }
                                        }}
                                        onOpenQuickLook={() => {
                                            if (quickLookCommandId === cmd.id) {
                                                closeQuickLook('programmatic');
                                            } else {
                                                openQuickLook(cmd.id);
                                            }
                                        }}
                                    />
                                ));

                                if (groupName) {
                                    return (
                                        <Command.Group key={groupName} heading={groupName}>
                                            {items}
                                        </Command.Group>
                                    );
                                }

                                return <React.Fragment key="ungrouped">{items}</React.Fragment>;
                            })}

                            {!page &&
                                entityResults.map(({ source, commands: entityCommands }) => (
                                    <Command.Group key={source.id} heading={source.groupLabel()}>
                                        {entityCommands.map((cmd) => (
                                            <GlobalCommandItem
                                                key={cmd.id}
                                                command={cmd}
                                                context={ctx}
                                                icon={source.icon}
                                                isMac={isMac}
                                                loading={loading === cmd.id}
                                                hasQuickLookOpen={quickLookCommandId === cmd.id}
                                                hasChildren={hasChildren(cmd)}
                                                onSelect={() => handleSelect(cmd)}
                                                onSelectedChange={(selected) => {
                                                    if (selected) {
                                                        setSelectedCommandId(cmd.id);
                                                    }
                                                }}
                                                onOpenQuickLook={() => {
                                                    if (quickLookCommandId === cmd.id) {
                                                        closeQuickLook('programmatic');
                                                    } else {
                                                        openQuickLook(cmd.id);
                                                    }
                                                }}
                                            />
                                        ))}
                                    </Command.Group>
                                ))}
                        </Command.List>
                    </Box>
                )}

                <Slide direction="left" in={!!quickLookCommand} mountOnEnter unmountOnExit>
                    <Box className="cmdk-quick-look-panel" sx={{ width: quickLookCommand?.quickLook?.width ?? 360 }}>
                        {quickLookCommand && (
                            <>
                                <Box className="cmdk-quick-look-panel-header">
                                    <Flexbox
                                        gap="2px"
                                        style={{ flex: 1, minWidth: 0 }}
                                        sx={{
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <Text variant="body-semibold" color={colorSystem.neutral[8]}>
                                            {quickLookCommand.label()}
                                        </Text>
                                        {quickLookCommand.description && (
                                            <Text variant="body-small" color={colorSystem.neutral[6]}>
                                                {quickLookCommand.description()}
                                            </Text>
                                        )}
                                    </Flexbox>
                                    <TertiaryIconButton
                                        onClick={() => closeQuickLook('programmatic')}
                                        size="small"
                                        aria-label={t`Close quick look`}
                                    >
                                        <XIcon />
                                    </TertiaryIconButton>
                                </Box>
                                <Box className="cmdk-quick-look-panel-content">
                                    {quickLookCommand.quickLook?.render(ctx)}
                                </Box>
                            </>
                        )}
                    </Box>
                </Slide>
            </Box>
            <Box
                sx={{
                    padding: '12px 20px',
                    borderTop: `1px solid ${colorSystem.neutral[2]}`,
                }}
            >
                <Flexbox
                    gap="8px"
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Flexbox
                        gap="12px"
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        <HintButton badge="↑↓" label={<Trans>navigate</Trans>} />
                        <HintButton badge="↵" label={<Trans>open</Trans>} />
                        <HintButton
                            badge={
                                <Flexbox
                                    gap="2px"
                                    sx={{
                                        alignItems: 'center',
                                    }}
                                >
                                    <span style={{ fontSize: '9px' }}>{isMac ? '⌘' : 'Ctrl'}</span>
                                    <span>↵</span>
                                </Flexbox>
                            }
                            label={<Trans>new tab</Trans>}
                        />
                        {pages.length > 0 ? (
                            <HintButton badge="esc" label={<Trans>back</Trans>} onClick={handleGoBack} />
                        ) : (
                            <HintButton badge="esc" label={<Trans>close</Trans>} onClick={handleClose} />
                        )}
                    </Flexbox>
                    <Flexbox
                        gap="12px"
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        {quickLookCommandId ? (
                            <HintButton
                                badge="←"
                                label={<Trans>close preview</Trans>}
                                onClick={() => closeQuickLook('programmatic')}
                            />
                        ) : selectedCommandHasQuickLook ? (
                            <HintButton
                                badge="→"
                                label={<Trans>preview</Trans>}
                                onClick={() => selectedCommandId && openQuickLook(selectedCommandId)}
                            />
                        ) : null}
                        <Flexbox
                            gap="2px"
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            <KeyboardBadge>{isMac ? '⌘' : 'Ctrl'}</KeyboardBadge>
                            <KeyboardBadge>K</KeyboardBadge>
                        </Flexbox>
                    </Flexbox>
                </Flexbox>
            </Box>
        </Command.Dialog>
    );
}

type GlobalCommandItemProps = {
    command: GlobalCommandDefinition;
    context: GlobalCommandContext;
    icon?: React.ReactNode;
    isMac: boolean;
    loading: boolean;
    hasQuickLookOpen: boolean;
    hasChildren: boolean;
    onSelect: () => void;
    onSelectedChange: (selected: boolean) => void;
    onOpenQuickLook: () => void;
};

function GlobalCommandItem({
    command,
    context,
    icon,
    isMac,
    loading,
    hasQuickLookOpen,
    hasChildren,
    onSelect,
    onSelectedChange,
    onOpenQuickLook,
}: GlobalCommandItemProps): JSX.Element {
    const isDisabled = typeof command.disabled === 'function' ? command.disabled(context) : (command.disabled ?? false);
    const itemRef = useRef<HTMLDivElement>(null);
    const lastSelectedRef = useRef<boolean | null>(null);
    const [isHoveringPreview, setIsHoveringPreview] = useState(false);
    const onSelectedChangeRef = useRef(onSelectedChange);
    onSelectedChangeRef.current = onSelectedChange;

    const hasQuickLook = !!command.quickLook;
    const label = command.label();
    const description = command.description?.();
    const commandIcon = command.icon ?? icon;

    const quickLookPreview = hasQuickLook
        ? typeof command.quickLook?.preview === 'function'
            ? command.quickLook.preview(context)
            : command.quickLook?.preview
        : undefined;

    // Selection lives in cmdk's `data-selected` attribute, so an observer watches it.
    useEffect(() => {
        const element = itemRef.current;
        if (!element) return;

        const handleSelectionChange = (selected: boolean) => {
            if (lastSelectedRef.current !== selected) {
                lastSelectedRef.current = selected;
                onSelectedChangeRef.current(selected);
            }
        };

        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.type === 'attributes' && mutation.attributeName === 'data-selected') {
                    const selected = element.getAttribute('data-selected') === 'true';
                    handleSelectionChange(selected);
                }
            }
        });

        observer.observe(element, { attributes: true, attributeFilter: ['data-selected'] });
        const initialSelected = element.getAttribute('data-selected') === 'true';
        handleSelectionChange(initialSelected);

        return () => observer.disconnect();
    }, []);

    const rawAccessory = typeof command.accessory === 'function' ? command.accessory(context) : command.accessory;
    const accessoryContent = isAccessoryCount(rawAccessory) ? formatAccessoryCount(rawAccessory) : rawAccessory;

    // cmdk's filter is off (`customFilter`), so `value` only identifies the item.
    const value = [command.id, label, description, ...(command.keywords ?? [])].filter(Boolean).join(' ');

    let defaultIcon: React.ReactNode = null;
    let hoverIcon: React.ReactNode = null;
    if (commandIcon) {
        if (isIconConfig(commandIcon)) {
            defaultIcon = commandIcon.default;
            hoverIcon = commandIcon.hover ?? commandIcon.default;
        } else {
            defaultIcon = commandIcon;
            hoverIcon = commandIcon;
        }
    }

    if (command.renderContent) {
        return (
            <Command.Item
                ref={itemRef}
                value={value}
                keywords={command.keywords}
                onSelect={onSelect}
                disabled={isDisabled || loading}
            >
                {commandIcon && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            flexShrink: 0,
                            marginTop: '4px',
                            color: colorSystem.neutral[6],
                        }}
                    >
                        <Box className="cmdk-icon-default">{defaultIcon}</Box>
                        <Box className="cmdk-icon-hover">{hoverIcon}</Box>
                    </Box>
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>{command.renderContent()}</Box>
                {loading && <CircularProgress size="small" sx={{ marginTop: '2px' }} />}
            </Command.Item>
        );
    }

    return (
        <Command.Item
            ref={itemRef}
            value={value}
            keywords={command.keywords}
            onSelect={onSelect}
            disabled={isDisabled || loading}
        >
            {commandIcon && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexShrink: 0,
                        marginTop: '2px',
                        color: colorSystem.neutral[6],
                    }}
                >
                    <Box className="cmdk-icon-default">{defaultIcon}</Box>
                    <Box className="cmdk-icon-hover">{hoverIcon}</Box>
                </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Text variant="body" color={colorSystem.neutral[9]}>
                    {label}
                </Text>
                {description && (
                    <Text variant="body-small" color={colorSystem.neutral[7]} style={{ lineHeight: 1.3 }}>
                        {description}
                    </Text>
                )}
            </Box>
            {loading && <CircularProgress size="small" sx={{ marginTop: '2px' }} />}
            {accessoryContent && !loading && !(hasQuickLook && !hasChildren) && (
                <Text variant="body-small" color={colorSystem.neutral[7]} style={{ marginTop: '2px', flexShrink: 0 }}>
                    {accessoryContent}
                </Text>
            )}
            {hasQuickLook && !loading && !hasChildren && (
                <Box
                    className="cmdk-quick-look-preview"
                    onMouseEnter={() => setIsHoveringPreview(true)}
                    onMouseLeave={() => setIsHoveringPreview(false)}
                    onClick={(e) => {
                        e.stopPropagation();
                        onOpenQuickLook();
                    }}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        marginTop: '2px',
                        padding: isHoveringPreview || hasQuickLookOpen ? '2px 8px' : '2px 4px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        backgroundColor: hasQuickLookOpen
                            ? colorSystem.primary[1]
                            : isHoveringPreview
                              ? colorSystem.neutral[2]
                              : 'transparent',
                        color: hasQuickLookOpen ? colorSystem.primary[7] : colorSystem.neutral[7],
                        '&:hover': {
                            backgroundColor: hasQuickLookOpen ? colorSystem.primary[2] : colorSystem.neutral[2],
                        },
                    }}
                >
                    {isHoveringPreview || hasQuickLookOpen ? (
                        <>
                            <Text
                                variant="body-small"
                                color={hasQuickLookOpen ? colorSystem.primary[7] : colorSystem.neutral[8]}
                                style={{ fontWeight: 500, whiteSpace: 'nowrap' }}
                            >
                                {hasQuickLookOpen ? t`Close` : t`Preview`}
                            </Text>
                            <ChevronRightIcon
                                size={16}
                                color={hasQuickLookOpen ? colorSystem.primary[6] : colorSystem.neutral[6]}
                                style={{
                                    transform: hasQuickLookOpen ? 'rotate(180deg)' : 'none',
                                    transition: 'transform 0.15s ease',
                                }}
                            />
                        </>
                    ) : (
                        <Text variant="body-small" color={colorSystem.neutral[6]} style={{ whiteSpace: 'nowrap' }}>
                            {quickLookPreview ?? accessoryContent ?? t`Preview`}
                        </Text>
                    )}
                </Box>
            )}
            {hasChildren && !loading && (
                <ChevronRightIcon size={18} color={colorSystem.neutral[6]} style={{ marginTop: '2px' }} />
            )}
            {command.shortcut && !loading && !hasChildren && !hasQuickLook && (
                <Flexbox
                    gap="2px"
                    sx={{
                        alignItems: 'center',
                        marginTop: '2px',
                    }}
                >
                    {command.shortcut.map((key, index) => (
                        <KeyboardBadge key={index}>{key === '⌘' && !isMac ? 'Ctrl' : key}</KeyboardBadge>
                    ))}
                </Flexbox>
            )}
        </Command.Item>
    );
}
